/**
 * Анон-MVP (#323/#12) — хуки данных публичной витрины (Tanstack Query v5
 * поверх @hsc/api-client). Все запросы без JWT, тенант резолвится по slug.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createBookingLead,
  getPublicHotelInfo,
  getPublicRoomTypes,
  listPublicEvents,
} from '@hsc/api-client';
import type {
  BookingLeadCreatedResponse,
  BookingLeadInput,
  EventListResponse,
  PublicHotelInfo,
  PublicRoomTypesResponse,
} from '@hsc/types';

/** GET /v1/public/{slug}/room-types. */
export function usePublicRoomTypes(slug: string | null) {
  return useQuery<PublicRoomTypesResponse>({
    queryKey: ['public', 'room-types', slug],
    queryFn: () => getPublicRoomTypes(slug as string),
    enabled: Boolean(slug),
  });
}

/** GET /v1/public/{slug}/events — только опубликованные (backend фильтрует). */
export function usePublicEvents(slug: string | null) {
  return useQuery<EventListResponse>({
    queryKey: ['public', 'events', slug],
    queryFn: () => listPublicEvents(slug as string),
    enabled: Boolean(slug),
  });
}

/** GET /v1/public/{slug}/hotel-info (контракт A). */
export function usePublicHotelInfo(slug: string | null) {
  return useQuery<PublicHotelInfo>({
    queryKey: ['public', 'hotel-info', slug],
    queryFn: () => getPublicHotelInfo(slug as string),
    enabled: Boolean(slug),
  });
}

/**
 * POST /v1/public/{slug}/booking-lead (контракт B).
 *
 * Защитный гард: если tenant ещё не резолвнут по поддомену (slug === null),
 * мутация не должна слать POST на `/public/null/...` — отклоняем явной
 * ошибкой, чтобы UI показал «попробуйте ещё раз», а не молча проглотил
 * сабмит (см. F-TN-ANON.05). На боевых поддоменах slug извлекается из host
 * синхронно (parseTenantSlugFromHost), поэтому штатный путь не затронут.
 */
export function useCreateBookingLead(slug: string | null) {
  return useMutation<BookingLeadCreatedResponse, Error, BookingLeadInput>({
    mutationFn: (body) => {
      if (!slug) {
        return Promise.reject(new Error('tenant_not_resolved'));
      }
      return createBookingLead(slug, body);
    },
  });
}
